import React, { useCallback, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import multiMonthPlugin from '@fullcalendar/multimonth';
import './modernized_app.css';
import OffstationLocationDisplay from './OffstationLocationDisplay';

const ATTENTION_COLOR = '#b42318';

const PLAN_MARKER_STYLES = {
  flying: {
    label: 'Local Use',
    color: '#2f6f9f',
    background: '#f2f8fc',
  },
  alert: {
    label: 'Alert',
    color: '#d6a21f',
    background: '#fff9df',
  },
  static: {
    label: 'Static',
    color: '#475467',
    background: '#f7f8fa',
  },
  custom: {
    label: 'Custom Task',
    color: '#5f6f7a',
    background: '#f7f9fb',
  },
  g081: {
    label: 'G081 9188-6 drivers',
    color: '#5b6fa7',
    background: '#f5f7fd',
  },
  g081Discrepancy: {
    label: 'G081 8035/781K driver',
    color: '#7a668f',
    background: '#f8f5fb',
  },
  return: {
    label: 'Offstation Est Return',
    color: '#8a5a2b',
    background: '#fbf4ed',
  },
  tailSwap: {
    label: 'Tail Swap',
    color: '#0f766e',
    background: '#eefaf8',
  },
  mission: {
    label: 'Deployment/TDY/exercise',
    color: '#8a6d3b',
    background: '#fbf8f0',
  },
  overdue: {
    label: 'Urgent / overdue',
    color: ATTENTION_COLOR,
    background: '#fef2f2',
  },
  maintenance: {
    label: 'Depot / Spare / other plan',
    color: '#5f6f7a',
    background: '#f7f9fb',
  },
  unavailable: {
    label: 'Unavailable',
    color: '#667085',
    background: '#f7f8fa',
  },
};

const CALENDAR_FILTERS = [
  ['flying', PLAN_MARKER_STYLES.flying],
  ['alert', PLAN_MARKER_STYLES.alert],
  ['static', PLAN_MARKER_STYLES.static],
  ['custom', PLAN_MARKER_STYLES.custom],
  ['g081', PLAN_MARKER_STYLES.g081],
  ['g081Discrepancy', PLAN_MARKER_STYLES.g081Discrepancy],
  ['return', PLAN_MARKER_STYLES.return],
  ['tailSwap', PLAN_MARKER_STYLES.tailSwap],
  ['mission', PLAN_MARKER_STYLES.mission],
  ['maintenance', PLAN_MARKER_STYLES.maintenance],
  ['unavailable', PLAN_MARKER_STYLES.unavailable],
  ['overdue', PLAN_MARKER_STYLES.overdue],
];

const TAIL_SWAP_EVENT_TYPES = new Set([
  'Departing - Tail Swap',
  'Returning - Tail Swap',
]);

const DAY_LANE_SOURCE_TYPES = new Set([
  'customTask',
  'g081Requirement',
  'g081Discrepancy',
]);

function isTailSwapEventType(type) {
  return TAIL_SWAP_EVENT_TYPES.has(String(type || '').trim());
}

function tailSwapLocationText(value, masked) {
  if (!value) return '';
  return masked ? 'Foreign OS' : value;
}

function planMarkerTone(eventType) {
  const type = String(eventType || '').trim().toLowerCase();
  if (isTailSwapEventType(eventType)) return 'tailSwap';
  if (type === 'alert') return 'alert';
  if (type === 'static') return 'static';
  if (type === 'local use') return 'flying';
  if (['deployment', 'tdy', 'exercise'].includes(type)) return 'mission';
  if (type === 'unavailable') return 'unavailable';
  return 'maintenance';
}

function calendarEventCategory(event) {
  const sourceType = event.extendedProps?.sourceType;
  if (sourceType === 'plan') return planMarkerTone(event.extendedProps?.eventType);
  if (sourceType === 'linkedTailSwap') return 'tailSwap';
  if (sourceType === 'estimatedReturn') return 'return';
  if (sourceType === 'customTask') return 'custom';
  if (sourceType === 'g081Requirement') return 'g081';
  if (sourceType === 'g081Discrepancy') return 'g081Discrepancy';
  if (sourceType === 'scheduled') return 'custom';
  return 'maintenance';
}

function eventNeedsAttention(event) {
  const props = event.extendedProps || {};
  return Boolean(props.scheduleWarning || props.reviewRequired || props.resourceWarnings?.length);
}

function eventDetails(event) {
  const props = event.extendedProps || {};
  return {
    title: event.title,
    sourceLabel: props.sourceLabel || '',
    tail: props.tail || '',
    eventType: props.eventType || '',
    start: event.start,
    end: event.end,
    allDay: event.allDay,
    condition: props.condition || '',
    details: props.details || '',
    planLocation: props.planLocation || '',
    tailSwapRole: props.tailSwapRole || '',
    swapWithTail: props.swapWithTail || '',
    swapWithLocation: props.swapWithLocation || '',
    swapForeignOsMasked: Boolean(props.swapForeignOsMasked),
    notes: props.notes || '',
    scheduleWarning: props.scheduleWarning || null,
    hsOs: props.hsOs || '',
    osLocation: props.osLocation || '',
  };
}

function formatEventRange(event) {
  if (!event?.start) return '--';
  const options = event.allDay
    ? { month: 'short', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' };
  const start = event.start.toLocaleString([], options);
  if (!event.end) return start;

  const displayEnd = new Date(event.end);
  if (event.allDay) displayEnd.setDate(displayEnd.getDate() - 1);
  const end = displayEnd.toLocaleString([], options);
  return start === end ? start : `${start} to ${end}`;
}

function localCalendarDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function isLongTimedWindow(event) {
  if (event.allDay || !event.start || !event.end) return false;
  const start = new Date(event.start);
  const end = new Date(event.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return false;
  return localCalendarDate(start) !== localCalendarDate(end) || end - start >= 12 * 60 * 60 * 1000;
}

function compactTimeGridWindow(event) {
  if (!isLongTimedWindow(event)) return event;
  const start = new Date(event.start);
  const end = new Date(event.end);

  const exclusiveEnd = new Date(end);
  if (end.getHours() || end.getMinutes() || end.getSeconds() || end.getMilliseconds()) {
    exclusiveEnd.setDate(exclusiveEnd.getDate() + 1);
  }
  return {
    ...event,
    start: localCalendarDate(start),
    end: localCalendarDate(exclusiveEnd),
    allDay: true,
    originalStart: event.start,
    originalEnd: event.end,
    originalAllDay: false,
  };
}

function startsAtLocalMidnight(value) {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date.getHours() === 0
    && date.getMinutes() === 0
    && date.getSeconds() === 0
    && date.getMilliseconds() === 0;
}

function placeUntimedMaintenanceInDayLanes(events, visibleDayStart) {
  let lane = 0;
  return events.map(event => {
    const sourceType = event.sourceType || event.extendedProps?.sourceType;
    const enteredStart = event.originalStart || event.plannedStartAt || event.start;
    if (!event.allDay || !DAY_LANE_SOURCE_TYPES.has(sourceType) || !startsAtLocalMidnight(enteredStart)) {
      return event;
    }

    const laneStart = new Date(visibleDayStart);
    laneStart.setHours(lane, 0, 0, 0);
    const laneEnd = new Date(laneStart);
    laneEnd.setHours(laneEnd.getHours() + 1);
    lane += 1;

    return {
      ...event,
      start: laneStart.toISOString(),
      end: laneEnd.toISOString(),
      allDay: false,
      originalStart: event.originalStart || event.start,
      originalEnd: event.originalEnd || event.end,
      originalAllDay: event.originalAllDay ?? event.allDay,
      untimedDayLane: true,
    };
  });
}

export default function CalendarTab({ api, user, selectedBase }) {
  const calendarRef = useRef(null);
  const previousViewRef = useRef('');
  const [activeCategories, setActiveCategories] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [feedback, setFeedback] = useState('');

  const loadEvents = useCallback(async (fetchInfo, successCallback, failureCallback) => {
    try {
      setFeedback('');
      const response = await api.get('/calendar-events', {
        params: {
          email: user.email,
          location: selectedBase,
          start: fetchInfo.startStr,
          end: fetchInfo.endStr,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
      const events = response.data.data || [];
      const viewType = calendarRef.current?.getApi().view.type;
      const timeGridEvents = viewType === 'timeGridWeek' || viewType === 'timeGridDay'
        ? events.map(compactTimeGridWindow)
        : events;
      successCallback(viewType === 'timeGridDay'
        ? placeUntimedMaintenanceInDayLanes(timeGridEvents, fetchInfo.start)
        : timeGridEvents);
    } catch (error) {
      console.error('Failed to load calendar events', error);
      setFeedback(error.response?.data?.message || 'Failed to load calendar events.');
      failureCallback(error);
    }
  }, [api, user.email, selectedBase]);

  const eventClassNames = useCallback(({ event }) => {
    const category = calendarEventCategory(event);
    const matchesSelection = activeCategories.some(activeCategory => (
      activeCategory === 'overdue' ? eventNeedsAttention(event) : category === activeCategory
    ));
    const classes = activeCategories.length
      ? [matchesSelection ? 'calendar-event--highlighted' : 'calendar-event--muted']
      : [];
    if (isLongTimedWindow(event)) classes.push('calendar-event--long-timed-window');
    return classes;
  }, [activeCategories]);

  const eventDidMount = useCallback(({ event, el }) => {
    const category = calendarEventCategory(event);
    const eventStyle = eventNeedsAttention(event)
      ? PLAN_MARKER_STYLES.overdue
      : PLAN_MARKER_STYLES[category];
    el.style.setProperty('--calendar-event-color', eventStyle.color);
    el.style.setProperty('--calendar-event-bg', eventStyle.background);
    const warning = [event.extendedProps?.scheduleWarning?.message, ...(event.extendedProps?.resourceWarnings || []).map(row => row.message)].filter(Boolean).join(' | ');
    const projectedSwapLocation = tailSwapLocationText(event.extendedProps?.planLocation, event.extendedProps?.swapForeignOsMasked);
    const swapText = event.extendedProps?.swapWithTail
      ? `Swap with/for ${event.extendedProps.swapWithTail}${event.extendedProps.swapWithLocation ? ` | ${event.extendedProps.swapWithLocation}` : ''}${projectedSwapLocation ? ` | ${projectedSwapLocation}` : ''}`
      : '';
    el.title = `${event.title}\n${event.extendedProps?.sourceLabel || ''}${swapText ? `\n${swapText}` : ''}${warning ? `\n${warning}` : ''}`;
  }, []);

  return (
    <section className="calendar-tab">
      <div className="calendar-tab__header ops-page-header">
        <div className="ops-page-header__copy">
          <h2 className="ops-page-header__title">Calendar</h2>
          <p className="calendar-tab__description">
            Status due dates, manual plans, and only G081 items marked Readiness Driver for {selectedBase}.
          </p>
        </div>
      </div>

      {feedback ? <div className="calendar-tab__feedback" role="alert">{feedback}</div> : null}

      <div className="calendar-tab__surface">
        <div className="calendar-tab__filter-legend" aria-label="Highlight calendar events by category">
          {CALENDAR_FILTERS.map(([category, style]) => (
            <button
              key={category}
              type="button"
              className={`calendar-filter-toggle${activeCategories.includes(category) ? ' is-selected' : ''}`}
              aria-pressed={activeCategories.includes(category)}
              onClick={() => setActiveCategories(current => (
                current.includes(category)
                  ? current.filter(item => item !== category)
                  : [...current, category]
              ))}
              title={`Highlight ${style.label} events`}
            >
              <i style={{ backgroundColor: style.color }} />
              {style.label}
            </button>
          ))}
          {activeCategories.length ? (
            <button
              type="button"
              className="calendar-filter-clear"
              onClick={() => setActiveCategories([])}
            >
              Clear highlights
            </button>
          ) : null}
        </div>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, multiMonthPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'timeGridDay,timeGridWeek,dayGridMonth,multiMonthQuarter,multiMonthYear',
          }}
          buttonText={{
            today: 'Today',
            day: 'Day',
            week: 'Week',
            month: 'Month',
            year: 'Year',
          }}
          views={{
            timeGridDay: {
              height: 720,
              slotDuration: '01:00:00',
              allDayText: 'ALL DAY',
              dayMaxEventRows: 4,
              slotEventOverlap: false,
              eventMaxStack: 3,
            },
            timeGridWeek: {
              height: 720,
              slotDuration: '01:00:00',
              allDayText: 'ALL DAY',
              dayMaxEventRows: 4,
              slotEventOverlap: false,
              eventMaxStack: 3,
            },
            multiMonthQuarter: {
              type: 'multiMonth',
              duration: { months: 3 },
              buttonText: 'Quarter',
            },
          }}
          events={loadEvents}
          datesSet={({ view }) => {
            if (previousViewRef.current && previousViewRef.current !== view.type) {
              calendarRef.current?.getApi().refetchEvents();
            }
            previousViewRef.current = view.type;
          }}
          eventClassNames={eventClassNames}
          eventDidMount={eventDidMount}
          eventClick={({ event }) => {
            const details = eventDetails(event);
            if (event.extendedProps?.originalStart) {
              details.start = new Date(event.extendedProps.originalStart);
              details.end = new Date(event.extendedProps.originalEnd);
              details.allDay = Boolean(event.extendedProps.originalAllDay);
            }
            setSelectedEvent(details);
          }}
          weekends
          firstDay={1}
          nowIndicator
          allDaySlot
          dayMaxEvents
          height="auto"
          eventDisplay="block"
        />
      </div>

      {selectedEvent ? (
        <aside className="calendar-event-detail" aria-live="polite">
          <div>
            <p className="calendar-event-detail__source">{selectedEvent.sourceLabel}</p>
            <h3>{selectedEvent.title}</h3>
            <p>{formatEventRange(selectedEvent)}</p>
          </div>
          <dl>
            <div><dt>Tail</dt><dd>{selectedEvent.tail || '--'}</dd></div>
            <div><dt>Event</dt><dd>{selectedEvent.eventType || '--'}</dd></div>
            {selectedEvent.scheduleWarning ? <div className="calendar-event-detail__warning"><dt>Review</dt><dd>{selectedEvent.scheduleWarning.message}</dd></div> : null}
            {selectedEvent.resourceWarnings?.length ? <div className="calendar-event-detail__warning"><dt>Equipment / Facilities</dt><dd>{selectedEvent.resourceWarnings.map((row, index) => <p key={index}>{row.message}{row.jobs?.length ? ` Conflicts: ${row.jobs.map(job => `${job.tail} / ${job.label}`).join('; ')}` : ''}</p>)}</dd></div> : null}
            {selectedEvent.condition ? <div><dt>Status</dt><dd>{selectedEvent.condition.replace('_', ' ')}</dd></div> : null}
            {selectedEvent.details ? <div><dt>Due</dt><dd>{selectedEvent.details}</dd></div> : null}
            {selectedEvent.swapWithTail ? <div><dt>Swap With/For</dt><dd>{selectedEvent.swapWithTail}</dd></div> : null}
            {selectedEvent.swapWithLocation ? <div><dt>Swap Base</dt><dd><OffstationLocationDisplay value={selectedEvent.swapWithLocation} emptyLabel="--" /></dd></div> : null}
            {selectedEvent.planLocation ? <div><dt>{isTailSwapEventType(selectedEvent.eventType) ? 'Projected Swap Location' : 'Plan Location'}</dt><dd>{selectedEvent.sourceLabel === 'Status Est Return' || isTailSwapEventType(selectedEvent.eventType) ? <OffstationLocationDisplay value={tailSwapLocationText(selectedEvent.planLocation, selectedEvent.swapForeignOsMasked)} emptyLabel="--" /> : selectedEvent.planLocation}</dd></div> : null}
            {selectedEvent.hsOs ? <div><dt>HS/OS</dt><dd>{selectedEvent.hsOs}{selectedEvent.osLocation ? <> | <OffstationLocationDisplay value={selectedEvent.osLocation} /></> : ''}</dd></div> : null}
            {selectedEvent.notes ? <div className="calendar-event-detail__notes"><dt>Notes</dt><dd>{selectedEvent.notes}</dd></div> : null}
          </dl>
          <button type="button" onClick={() => setSelectedEvent(null)}>Close Details</button>
        </aside>
      ) : null}
    </section>
  );
}
